import React, { useEffect, useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Divider,
    CircularProgress,
    Button,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useNavigate, useParams } from "react-router-dom";
import { getCourseData } from "../../services/CourseService";
import { getJobData } from "../../services/PartTimeJobDetailsService";

/* ---------------- COMPONENTS ---------------- */

const OverviewRow = ({ label, value }) => {
  const getFormattedValue = () => {
    if (!value) return "-";

    // ✅ Email
    if (label.toLowerCase().includes("email")) {
      return (
        <a href={`mailto:${value}`} style={{ color: "#1976d2" }}>
          {value}
        </a>
      );
    }

    // ✅ Website
    if (label.toLowerCase().includes("website")) {
      const url = value.startsWith("http") ? value : `https://${value}`;
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
          {value}
        </a>
      );
    }

    // ✅ Location (Google Maps)
    if (label.toLowerCase().includes("location")) {
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}`;
      return (
        <a href={mapUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#1976d2" }}>
          {value}
        </a>
      );
    }

    // default
    return value;
  };

  return (
    <>
      <Grid size={{ xs: 12, md: 4 }}>
        <Typography fontWeight={700}>{label}</Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Typography sx={{ wordBreak: "break-all" }}>
          {getFormattedValue()}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider sx={{ borderColor: "#e0e0e0" }} />
      </Grid>
    </>
  );
};

/* ---------------- PAGE ---------------- */

const JobDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [jobData, setJobData] = useState(null);

    useEffect(() => {
        if (!id) return;

        const fetchJobData = async () => {
            try {
                const res = await getJobData(id);
                if (res?.data?.success) {
                    setJobData(res.data.data);
                }
            } catch (err) {
                console.error("❌ Error fetching program:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchJobData();
    }, [id]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
                <CircularProgress color="warning" />
            </Box>
        );
    }

    if (!jobData) {
        return (
            <Box sx={{ textAlign: "center", mt: 10 }}>
                <Typography>No job details data found.</Typography>
            </Box>
        );
    }

    return (
        <>
            {/* 🔶 HERO SECTION */}
            <Box
                sx={{
                    background: "linear-gradient(90deg, #053770 0%, #002045 100%)",
                    color: "#fff",
                    py: { xs: 3, sm: 4, md: 6 },
                }}
            >
                <Container maxWidth="lg" sx={{
                    px: { xs: 3, sm: 4, md: 6 },
                    paddingLeft: { xs: "24px !important", md: "48px !important" },
                    paddingRight: { xs: "24px !important", md: "48px !important" }
                }}>
                    <Button
                        color="#ffffff"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/part-time-jobs")} // <-- navigate back
                        sx={{ mb: 2 }}
                    >
                        Back
                    </Button>
                    <Typography variant="h3" sx={{ fontSize: { xs: "22px", sm: "26px", md: "30px" } }} fontWeight={700}>
                        {jobData.job_title}
                    </Typography>

                    <Typography variant="subtitle1" sx={{ mt: 1 , fontSize: { xs: "14px", sm: "16px" } }}>
                        {jobData.job_desc}
                    </Typography>

                    {/* <Typography
                        variant="body2"
                        sx={{
                            mt: 2,
                            opacity: 0.9,
                            borderTop: "1px solid #ffffff",
                            pt: 1,
                            maxWidth: 120,
                        }}
                    >
                        Overview
                    </Typography> */}
                </Container>
            </Box>

            {/* 🔷 CONTENT */}
            <Container maxWidth="lg" sx={{
                py: { xs: 3, sm: 4, md: 6 },
                px: { xs: 3, sm: 4, md: 6 },
                paddingLeft: { xs: "24px !important", md: "48px !important" },
                paddingRight: { xs: "24px !important", md: "48px !important" }
            }}>
                <Typography
                    variant="h4"
                    fontWeight={600}
                    sx={{ mb: { xs: 2, sm: 4 }, color: "#002045", fontSize: { xs: "22px", sm: "30px" } }}
                >
                    Overview
                </Typography>

                <Grid container spacing={2}>
                    <OverviewRow label="Company Email" value={jobData.company_email} />
                    <OverviewRow label="Company Mobile" value={jobData.company_mobile} />
                    <OverviewRow label="Job Location" value={jobData.job_location} />
                    <OverviewRow label="Job Website" value={jobData.job_website} />
                </Grid>
            </Container>
        </>
    );
};

export default JobDetailPage;